import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Brain, Dumbbell, ShoppingBag, Users, CreditCard, TrendingUp, Calendar, ChevronRight, Activity, Bell, Trophy, MessageCircle, Droplets, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Navbar } from "../components/Navbar";
import { ProtectedRoute } from "../components/ProtectedRoute";
import api from "../lib/api";

const DashboardContent = () => {
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [todayReminders, setTodayReminders] = useState([]);
  const [goalSummary, setGoalSummary] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (stored) setUser(JSON.parse(stored));

    const fetchData = async () => {
      try {
        const [plansRes, statsRes, profileRes, remindersRes, goalRes] = await Promise.all([
          api.get("/vita-ai/plans").catch(() => ({ data: [] })),
          api.get("/stats").catch(() => ({ data: {} })),
          api.get("/users/profile").catch(() => ({ data: null })),
          api.get("/reminders/today").catch(() => ({ data: [] })),
          api.get("/goals/summary").catch(() => ({ data: null })),
        ]);
        setPlans(plansRes.data || []);
        setStats(statsRes.data || {});
        setTodayReminders(remindersRes.data || []);
        setGoalSummary(goalRes.data);
        if (profileRes.data) {
          setUser(profileRes.data);
          localStorage.setItem("vita_user", JSON.stringify(profileRes.data));
        }
      } catch (err) { console.error('Error:', err.message || err); }
    };
    fetchData();
  }, []);

  const tierLabel = {
    pending_trial: "Start Free Trial",
    free_trial: "Free Trial",
    premium: "Premium",
    super_premium: "Super Premium",
    free: "Free",
  };

  const tierColor = {
    pending_trial: "bg-amber-100 text-amber-800",
    free_trial: "bg-amber-100 text-amber-800",
    premium: "bg-[#D4F4DD] text-[#0B6E4F]",
    super_premium: "bg-[#1B3A5C] text-white",
    free: "bg-slate-100 text-slate-600",
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Welcome Header */}
        <div className="mb-10">
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0F172A]">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-slate-500">Your health dashboard</p>
            <Badge className={tierColor[user?.subscription_tier] || tierColor.free} data-testid="subscription-badge">
              {tierLabel[user?.subscription_tier] || "Free"}
            </Badge>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {[
            { icon: Brain, label: "AI Plans", value: plans.length, color: "text-[#0B6E4F]", bg: "bg-[#F2F9F6]" },
            { icon: Users, label: "Community Members", value: stats.total_users || 0, color: "text-[#1B3A5C]", bg: "bg-blue-50" },
            { icon: ShoppingBag, label: "Market Products", value: stats.total_products || 0, color: "text-amber-600", bg: "bg-amber-50" },
            { icon: Activity, label: "Posts", value: stats.total_posts || 0, color: "text-purple-600", bg: "bg-purple-50" },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid={`stat-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`${s.bg} p-2.5 rounded-xl`}>
                  <s.icon className={`h-5 w-5 ${s.color}`} />
                </div>
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="font-['Outfit'] text-2xl font-bold text-[#0F172A]">{s.value}</p>
              <p className="text-sm text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions + Recent Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A]">Quick Actions</h3>
            {[
              { to: "/vita-ai", icon: Brain, label: "Generate AI Plan", desc: "Get personalized health plan", color: "bg-[#0B6E4F]" },
              { to: "/community", icon: Users, label: "Visit Community", desc: "Join discussions", color: "bg-[#1B3A5C]" },
              { to: "/marketplace", icon: ShoppingBag, label: "Browse Market", desc: "Shop health products", color: "bg-amber-600" },
              { to: "/challenges", icon: Trophy, label: "Challenges", desc: "Join monthly challenges", color: "bg-orange-500" },
              { to: "/progress", icon: TrendingUp, label: "Track Progress", desc: "Log daily metrics", color: "bg-blue-600" },
              { to: "/messages", icon: MessageCircle, label: "Messages", desc: "Chat with members", color: "bg-indigo-600" },
              { to: "/reminders", icon: Bell, label: "Reminders", desc: "Health reminders", color: "bg-purple-600" },
              { to: "/pricing", icon: CreditCard, label: "Manage Plan", desc: "Upgrade subscription", color: "bg-slate-600" },
            ].map((a, i) => (
              <Link key={i} to={a.to} data-testid={`quick-action-${i}`}>
                <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex items-center gap-4 group">
                  <div className={`${a.color} w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <a.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0F172A]">{a.label}</p>
                    <p className="text-xs text-slate-500">{a.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                </div>
              </Link>
            ))}
          </div>

          {/* Recent Plans */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A]">Recent AI Plans</h3>
              <Link to="/vita-ai" className="text-sm text-[#0B6E4F] hover:underline font-medium">View All</Link>
            </div>
            {plans.length > 0 ? (
              <div className="space-y-4">
                {plans.slice(0, 3).map((plan, i) => (
                  <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid={`recent-plan-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-[#F2F9F6] p-2 rounded-lg">
                          <Brain className="h-4 w-4 text-[#0B6E4F]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#0F172A]">
                            {plan.input?.fitness_goal || "Health Plan"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(plan.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Link to={`/vita-ai?plan=${plan.plan_id}`}>
                        <Button variant="ghost" size="sm" className="text-[#0B6E4F] text-xs">
                          View <ChevronRight className="ml-1 h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                    {plan.plan_data?.summary && (
                      <p className="text-sm text-slate-600 line-clamp-2">{plan.plan_data.summary}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl p-12 shadow-sm text-center">
                <Brain className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No AI plans yet</p>
                <Link to="/vita-ai">
                  <Button className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 font-semibold">
                    Generate Your First Plan
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Today's Reminders & Weekly Progress */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Today's Reminders */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A]">Today's Reminders</h3>
              <Link to="/reminders" className="text-sm text-[#0B6E4F] hover:underline font-medium">Manage</Link>
            </div>
            {todayReminders.length > 0 ? (
              <div className="space-y-3">
                {todayReminders.slice(0, 5).map((rem, i) => (
                  <div key={i} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex items-center gap-3" data-testid={`dash-reminder-${i}`}>
                    <div className={`p-2 rounded-lg ${rem.reminder_type === "meal" ? "bg-green-50" : rem.reminder_type === "water" ? "bg-blue-50" : rem.reminder_type === "workout" ? "bg-orange-50" : "bg-purple-50"}`}>
                      {rem.reminder_type === "water" ? <Droplets className="h-4 w-4 text-blue-500" /> :
                       rem.reminder_type === "workout" ? <Dumbbell className="h-4 w-4 text-orange-500" /> :
                       rem.reminder_type === "meal" ? <Flame className="h-4 w-4 text-green-500" /> :
                       <Bell className="h-4 w-4 text-purple-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0F172A]">{rem.title}</p>
                    </div>
                    <span className="text-xs font-medium text-[#0B6E4F]">{rem.time}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl p-8 shadow-sm text-center">
                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 mb-3">No reminders set</p>
                <Link to="/reminders"><Button size="sm" variant="outline" className="rounded-full border-[#0B6E4F] text-[#0B6E4F] text-xs">Set Up Reminders</Button></Link>
              </div>
            )}
          </div>

          {/* Weekly Progress */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A]">This Week</h3>
              <Link to="/progress" className="text-sm text-[#0B6E4F] hover:underline font-medium">Full View</Link>
            </div>
            {goalSummary ? (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Avg Calories", value: goalSummary.weekly_avg_calories, unit: "cal", icon: Flame, color: "text-orange-500", bg: "bg-orange-50" },
                  { label: "Avg Water", value: goalSummary.weekly_avg_water, unit: "L", icon: Droplets, color: "text-blue-500", bg: "bg-blue-50" },
                  { label: "Avg Workout", value: goalSummary.weekly_avg_workout, unit: "min", icon: Dumbbell, color: "text-green-500", bg: "bg-green-50" },
                  { label: "Badges", value: goalSummary.badges_earned, unit: "", icon: Trophy, color: "text-amber-500", bg: "bg-amber-50" },
                ].map((s, i) => (
                  <div key={i} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid={`dash-progress-${i}`}>
                    <div className={`${s.bg} p-2 rounded-lg w-fit mb-2`}><s.icon className={`h-4 w-4 ${s.color}`} /></div>
                    <p className="font-['Outfit'] text-lg font-bold text-[#0F172A]">{s.value}<span className="text-xs text-slate-400 ml-0.5">{s.unit}</span></p>
                    <p className="text-xs text-slate-400">{s.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl p-8 shadow-sm text-center">
                <TrendingUp className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 mb-3">Start tracking your progress</p>
                <Link to="/progress"><Button size="sm" variant="outline" className="rounded-full border-[#0B6E4F] text-[#0B6E4F] text-xs">Log Today</Button></Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
}
